import React from 'react';
import AnimatedNumber from './AnimatedNumber';

export type HeroAccent = 'violet' | 'emerald' | 'amber' | 'sky' | 'rose' | 'fuchsia';

interface Props {
  label: string;
  value: number | null | undefined;
  sub?: string | null;
  accent?: HeroAccent;
  icon: React.ReactNode;
  delayMs?: number;
  format?: (n: number) => string;
  size?: 'md' | 'lg';
  durationMs?: number;
}

const ACCENTS: Record<HeroAccent, string> = {
  violet: 'from-violet-600/20 to-violet-900/0 border-violet-700/30 text-violet-300',
  emerald: 'from-emerald-600/20 to-emerald-900/0 border-emerald-700/30 text-emerald-300',
  amber: 'from-amber-600/20 to-amber-900/0 border-amber-700/30 text-amber-300',
  sky: 'from-sky-600/20 to-sky-900/0 border-sky-700/30 text-sky-300',
  rose: 'from-rose-600/20 to-rose-900/0 border-rose-700/30 text-rose-300',
  fuchsia: 'from-fuchsia-600/20 to-fuchsia-900/0 border-fuchsia-700/30 text-fuchsia-300',
};

const ACCENT_GLOW: Record<HeroAccent, string> = {
  violet: 'shadow-violet-900/40',
  emerald: 'shadow-emerald-900/40',
  amber: 'shadow-amber-900/40',
  sky: 'shadow-sky-900/40',
  rose: 'shadow-rose-900/40',
  fuchsia: 'shadow-fuchsia-900/40',
};

export default function HeroStat({
  label,
  value,
  sub,
  accent = 'violet',
  icon,
  delayMs = 0,
  format,
  size = 'md',
  durationMs,
}: Props): React.ReactElement {
  const numberSize = size === 'lg' ? 'text-4xl md:text-5xl' : 'text-3xl';
  return (
    <div
      className={`ss-fade-up relative overflow-hidden bg-gradient-to-br ${ACCENTS[accent]} bg-[#181831] border rounded-2xl p-5 transition-all duration-300 hover:scale-[1.02] hover:shadow-lg ${ACCENT_GLOW[accent]} cursor-default`}
      style={{ animationDelay: `${delayMs}ms` }}
    >
      <div className="pointer-events-none absolute -top-12 -right-12 w-32 h-32 rounded-full bg-current opacity-10 blur-2xl" />
      <div className="relative flex items-start justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{label}</p>
          <p className={`mt-2 ${numberSize} font-bold text-white tabular-nums`}>
            <AnimatedNumber value={value} format={format} duration={durationMs} />
          </p>
          {sub && <p className="mt-1 text-xs text-gray-500">{sub}</p>}
        </div>
        <div className="w-9 h-9 rounded-lg bg-black/30 flex items-center justify-center">{icon}</div>
      </div>
    </div>
  );
}
